import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Compass,
  Crosshair,
  Loader2,
  Move3d,
  Play,
  RotateCw,
  Ruler,
  X,
} from 'lucide-react';
import type { CalibrationTarget, RobotEvent } from '@fabricos/protocol-ts';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { sendCommand } from '@/hooks/useHubSocket';
import { useFleet } from '@/store/fleet';
import { cn } from '@/lib/utils';
import { ACCEL_POSES, DroneOrientationSvg, type AccelPose } from './DroneOrientationSvg';

/**
 * Calibration center — QGC-grade guided flows over the Hub's calibration
 * command surface:
 *   - Accelerometer: six-position wizard. ArduPilot prompts per orientation
 *     (STATUSTEXT) and waits for `accel_cal_pos` confirms — the wizard shows
 *     the orientation art, the operator clicks "In position".
 *   - Compass: onboard mag cal with live per-compass coverage (MAG_CAL_PROGRESS)
 *     and the fitness verdict (MAG_CAL_REPORT). Reboot chip on success.
 *   - Gyro / Level: one-shot cals with vehicle-text feedback.
 * All flows are refused by the Hub while armed.
 */
export function CalibrationCenter({ robotId }: { robotId: string }) {
  const armed = useFleet((s) => s.states[robotId]?.arm === 'armed');
  return (
    <div className="mx-auto max-w-[860px] space-y-4 p-4">
      {armed && (
        <div className="flex items-center gap-2 rounded border border-status-critical/40 bg-status-critical/10 px-3 py-2 text-[12px] text-status-critical">
          <AlertTriangle className="h-4 w-4 flex-none" />
          Vehicle is ARMED — every calibration is refused by the Hub until you disarm.
        </div>
      )}
      <AccelWizard robotId={robotId} armed={armed} />
      <CompassWizard robotId={robotId} armed={armed} />
      <div className="grid gap-4 sm:grid-cols-2">
        <QuickCal
          robotId={robotId}
          armed={armed}
          target="gyro"
          icon={Activity}
          title="Gyroscope"
          blurb="Keep the vehicle completely still on a solid surface. Takes a few seconds."
        />
        <QuickCal
          robotId={robotId}
          armed={armed}
          target="level"
          icon={Ruler}
          title="Level horizon"
          blurb="Vehicle sitting in its level flight attitude. Trims the horizon without a full six-sided run."
        />
      </div>
    </div>
  );
}

function calCommand(robotId: string, target: CalibrationTarget) {
  sendCommand({
    kind: 'sensor_calibration',
    id: `cal-${target}-${Date.now()}`,
    robot_id: robotId,
    priority: 'normal',
    target,
  });
}

/** Vehicle STATUSTEXT lines since a timestamp — the autopilot narrates
 *  calibration over text events. */
function useVehicleText(robotId: string, sinceMs: number | null): string[] {
  const events = useFleet((s) => s.events);
  return useMemo(() => {
    if (sinceMs == null) return [];
    return events
      .filter(
        (e): e is Extract<RobotEvent, { kind: 'text' }> =>
          e.kind === 'text' && e.robot_id === robotId && Number(e.ts_ms) >= sinceMs,
      )
      .map((e) => e.message)
      .reverse();
  }, [events, robotId, sinceMs]);
}

/* =================================================================== */
/* Accelerometer — six-position wizard                                  */
/* =================================================================== */

type AccelPhase = 'idle' | 'running' | 'done' | 'failed';

function AccelWizard({ robotId, armed }: { robotId: string; armed: boolean }) {
  const [phase, setPhase] = useState<AccelPhase>('idle');
  const [step, setStep] = useState(0); // index into ACCEL_POSES
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const texts = useVehicleText(robotId, startedAt);

  // The autopilot announces the outcome over STATUSTEXT.
  useEffect(() => {
    if (phase !== 'running') return;
    const joined = texts.join(' · ').toLowerCase();
    if (joined.includes('calibration successful')) setPhase('done');
    else if (joined.includes('calibration failed')) setPhase('failed');
  }, [texts, phase]);

  function start() {
    calCommand(robotId, 'accel');
    setStep(0);
    setStartedAt(Date.now());
    setPhase('running');
  }
  function confirmPosition() {
    const pose = ACCEL_POSES[step]!;
    sendCommand({
      kind: 'accel_cal_pos',
      id: `accelpos-${pose.pos}-${Date.now()}`,
      robot_id: robotId,
      priority: 'normal',
      position: pose.pos,
    });
    if (step < ACCEL_POSES.length - 1) setStep(step + 1);
  }
  function cancel() {
    calCommand(robotId, 'cancel');
    setPhase('idle');
    setStartedAt(null);
  }

  const current = ACCEL_POSES[step]!;

  return (
    <section className="overflow-hidden rounded-lg border border-border-default bg-surface-1">
      <header className="flex items-center gap-2.5 border-b border-border-subtle px-4 py-3">
        <Move3d className="h-4 w-4 text-accent" />
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-semibold text-fg-primary">Accelerometer</div>
          <div className="text-[11px] text-fg-muted">
            Six-position calibration — you'll hold the vehicle in each orientation and confirm.
          </div>
        </div>
        {phase === 'done' && <Badge tone="good">calibrated</Badge>}
        {phase === 'failed' && <Badge tone="critical">failed</Badge>}
        {phase === 'idle' || phase === 'done' || phase === 'failed' ? (
          <Button size="sm" onClick={start} disabled={armed}>
            <Play className="h-3.5 w-3.5" />
            {phase === 'idle' ? 'Start' : 'Restart'}
          </Button>
        ) : (
          <Button size="sm" variant="secondary" onClick={cancel}>
            <X className="h-3.5 w-3.5" />
            Cancel
          </Button>
        )}
      </header>

      {phase === 'running' && (
        <div className="px-4 py-4">
          {/* Step rail */}
          <ol className="mb-4 flex items-center gap-1">
            {ACCEL_POSES.map((p, i) => (
              <li key={p.pos} className="flex flex-1 items-center gap-1">
                <span
                  className={cn(
                    'grid h-6 w-6 flex-none place-items-center rounded-full border font-mono text-[10px] font-bold',
                    i < step && 'border-status-good bg-status-good/15 text-status-good',
                    i === step && 'border-accent bg-accent/15 text-accent',
                    i > step && 'border-border-default text-fg-muted',
                  )}
                >
                  {i < step ? <CheckCircle2 className="h-3.5 w-3.5" /> : p.pos}
                </span>
                {i < ACCEL_POSES.length - 1 && (
                  <span
                    className={cn('h-px flex-1', i < step ? 'bg-status-good/60' : 'bg-border-default')}
                  />
                )}
              </li>
            ))}
          </ol>

          <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center">
            {/* Orientation art: previous → current → next */}
            <div className="flex items-center gap-2">
              <div className="hidden opacity-30 sm:block">
                {step > 0 && <DroneOrientationSvg pose={ACCEL_POSES[step - 1]!.pos} size={84} />}
              </div>
              <div className="rounded-lg border border-accent/40 bg-accent/5 p-2">
                <DroneOrientationSvg pose={current.pos} active />
              </div>
              <div className="hidden opacity-30 sm:block">
                {step < ACCEL_POSES.length - 1 && (
                  <DroneOrientationSvg pose={ACCEL_POSES[step + 1]!.pos} size={84} />
                )}
              </div>
            </div>
            <div className="min-w-0 flex-1 text-center sm:text-left">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-fg-muted">
                step {step + 1} of {ACCEL_POSES.length}
              </div>
              <div className="mt-0.5 text-[15px] font-semibold text-fg-primary">{current.label}</div>
              <p className="mt-1 text-[12px] leading-relaxed text-fg-secondary">
                {current.instruction}. Hold it perfectly still, then confirm.
              </p>
              <Button className="mt-3" onClick={confirmPosition}>
                <CheckCircle2 className="h-4 w-4" />
                Vehicle is in position
              </Button>
            </div>
          </div>
          <VehicleSays texts={texts} />
        </div>
      )}

      {phase === 'failed' && (
        <p className="px-4 py-3 text-[12px] text-status-critical">
          The autopilot rejected the run — usually the vehicle moved during a step. Restart and keep
          each pose rock-steady.
        </p>
      )}
      {phase === 'done' && (
        <p className="px-4 py-3 text-[12px] text-fg-secondary">
          Offsets stored. A <span className="text-fg-primary">reboot</span> is recommended before
          flying.
        </p>
      )}
    </section>
  );
}

/* =================================================================== */
/* Compass — onboard mag cal with live coverage                         */
/* =================================================================== */

const MAG_STATUS: Record<number, { label: string; tone: 'good' | 'warn' | 'critical' | 'default' }> = {
  0: { label: 'not started', tone: 'default' },
  1: { label: 'waiting', tone: 'default' },
  2: { label: 'sampling', tone: 'warn' },
  3: { label: 'fitting', tone: 'warn' },
  4: { label: 'success', tone: 'good' },
  5: { label: 'failed', tone: 'critical' },
  6: { label: 'bad orientation', tone: 'critical' },
  7: { label: 'bad radius', tone: 'critical' },
};

function CompassWizard({ robotId, armed }: { robotId: string; armed: boolean }) {
  const [running, setRunning] = useState(false);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const events = useFleet((s) => s.events);

  const { progress, reports } = useMemo(() => {
    const progress = new Map<number, number>();
    const reports = new Map<number, { status: number; fitness: number }>();
    if (startedAt == null) return { progress, reports };
    // events are newest-first; walk oldest→newest so latest wins.
    for (let i = events.length - 1; i >= 0; i--) {
      const e = events[i]!;
      if (e.robot_id !== robotId || Number(e.ts_ms) < startedAt) continue;
      if (e.kind === 'mag_cal_progress') progress.set(e.compass_id, e.completion_pct);
      if (e.kind === 'mag_cal_report') {
        reports.set(e.compass_id, { status: e.cal_status, fitness: e.fitness });
      }
    }
    return { progress, reports };
  }, [events, robotId, startedAt]);

  const allReported = reports.size > 0 && Array.from(progress.keys()).every((id) => reports.has(id));
  const anySuccess = Array.from(reports.values()).some((r) => r.status === 4);

  function start() {
    calCommand(robotId, 'compass');
    setStartedAt(Date.now());
    setRunning(true);
  }
  function stop() {
    calCommand(robotId, 'cancel');
    setRunning(false);
  }
  function reboot() {
    sendCommand({ kind: 'reboot', id: `reboot-${Date.now()}`, robot_id: robotId, priority: 'normal' });
  }

  const compassIds = Array.from(new Set([...progress.keys(), ...reports.keys()])).sort();

  return (
    <section className="overflow-hidden rounded-lg border border-border-default bg-surface-1">
      <header className="flex items-center gap-2.5 border-b border-border-subtle px-4 py-3">
        <Compass className="h-4 w-4 text-accent" />
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-semibold text-fg-primary">Compass</div>
          <div className="text-[11px] text-fg-muted">
            Onboard mag cal — rotate the vehicle through all orientations until every bar fills.
          </div>
        </div>
        {anySuccess && allReported && <Badge tone="good">calibrated</Badge>}
        {!running ? (
          <Button size="sm" onClick={start} disabled={armed}>
            <Play className="h-3.5 w-3.5" />
            Start
          </Button>
        ) : (
          <Button size="sm" variant="secondary" onClick={stop}>
            <X className="h-3.5 w-3.5" />
            Stop
          </Button>
        )}
      </header>

      {(running || compassIds.length > 0) && (
        <div className="space-y-3 px-4 py-4">
          {compassIds.length === 0 && (
            <div className="flex items-center gap-2 text-[12px] text-fg-secondary">
              <Loader2 className="h-4 w-4 animate-spin text-accent" />
              Waiting for the autopilot… start rotating the vehicle slowly around every axis.
            </div>
          )}
          {compassIds.map((id) => {
            const pct = progress.get(id) ?? 0;
            const report = reports.get(id);
            const verdict = report ? MAG_STATUS[report.status] ?? MAG_STATUS[0]! : null;
            return (
              <div key={id}>
                <div className="mb-1 flex items-baseline gap-2 font-mono text-[11px]">
                  <span className="text-fg-primary">compass {id}</span>
                  <span className="text-fg-muted">{report ? '' : `${pct}%`}</span>
                  {verdict && (
                    <span className="ml-auto">
                      <Badge tone={verdict.tone}>
                        {verdict.label}
                        {report && report.status === 4 && ` · fit ${report.fitness.toFixed(1)}`}
                      </Badge>
                    </span>
                  )}
                </div>
                <div className="h-2 overflow-hidden rounded bg-surface-2">
                  <div
                    className={cn(
                      'h-full transition-[width] duration-300',
                      report?.status === 4
                        ? 'bg-status-good'
                        : report && report.status >= 5
                          ? 'bg-status-critical'
                          : 'bg-accent',
                    )}
                    style={{ width: `${report ? 100 : pct}%` }}
                  />
                </div>
              </div>
            );
          })}
          {anySuccess && allReported && (
            <div className="flex items-center justify-between rounded border border-status-good/40 bg-status-good/10 px-3 py-2">
              <span className="text-[12px] text-fg-secondary">
                Offsets stored — reboot the autopilot to apply them.
              </span>
              <Button size="sm" variant="secondary" onClick={reboot} disabled={armed}>
                <RotateCw className="h-3.5 w-3.5" />
                Reboot
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/* =================================================================== */
/* One-shot cals (gyro, level)                                          */
/* =================================================================== */

function QuickCal({
  robotId,
  armed,
  target,
  icon: Icon,
  title,
  blurb,
}: {
  robotId: string;
  armed: boolean;
  target: CalibrationTarget;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  blurb: string;
}) {
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const texts = useVehicleText(robotId, startedAt);
  const busyRef = useRef<number | null>(null);
  const [busy, setBusy] = useState(false);

  function run() {
    calCommand(robotId, target);
    setStartedAt(Date.now());
    setBusy(true);
    if (busyRef.current) window.clearTimeout(busyRef.current);
    busyRef.current = window.setTimeout(() => setBusy(false), 5000);
  }
  useEffect(() => () => {
    if (busyRef.current) window.clearTimeout(busyRef.current);
  }, []);

  return (
    <section className="rounded-lg border border-border-default bg-surface-1 p-4">
      <div className="flex items-start gap-2.5">
        <Icon className="h-4 w-4 flex-none text-accent" />
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-semibold text-fg-primary">{title}</div>
          <p className="mt-0.5 text-[11.5px] leading-relaxed text-fg-secondary">{blurb}</p>
        </div>
        <Button size="sm" onClick={run} disabled={armed || busy}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Crosshair className="h-3.5 w-3.5" />}
          Run
        </Button>
      </div>
      <VehicleSays texts={texts} />
    </section>
  );
}

/** Latest autopilot narration lines — the ground truth during any cal. */
function VehicleSays({ texts }: { texts: string[] }) {
  if (texts.length === 0) return null;
  return (
    <div className="mt-3 rounded border border-border-subtle bg-base/60 px-2.5 py-1.5">
      <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-fg-muted">vehicle says</div>
      <ul className="mt-0.5 space-y-0.5">
        {texts.slice(0, 3).map((t, i) => (
          <li key={i} className={cn('font-mono text-[10.5px]', i === 0 ? 'text-fg-primary' : 'text-fg-muted')}>
            {t}
          </li>
        ))}
      </ul>
    </div>
  );
}
